import React, { useId, useRef, useState } from 'react';
import { MOBILE_QUERY } from '../breakpoints';
import CollapsibleSection from '../collapsibleSection';
import CookiePolicyLink from '../cookiePolicyLink';
import { COOKIE_TEXT } from '../text';
import ToggleSwitch from '../toggleSwitch';
import useMediaQuery from '../useMediaQuery';
import useDialogFocus from './useDialogFocus';

const TAB_KEYS = {
    ArrowDown: 1,
    ArrowRight: 1,
    ArrowUp: -1,
    ArrowLeft: -1,
};

function CloseIcon() {
    return (
        <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true" focusable="false">
            <path d="M8 8l8 8M16 8l-8 8" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
    );
}

function CookieDialog({ logoSrc, logoAlt, cookiePolicyUrl, analyticsEnabled, returnFocusRef, onClose, onEnableAll, onSave }) {
    const { dialog, switch: switchText } = COOKIE_TEXT;
    const { sections } = dialog;
    const idPrefix = useId();
    const dialogRef = useRef(null);
    const tabRefs = useRef([]);
    const isMobile = useMediaQuery(MOBILE_QUERY);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isAnalyticsOn, setIsAnalyticsOn] = useState(analyticsEnabled);
    useDialogFocus(dialogRef, onClose, returnFocusRef);

    function hasSwitch(section) {
        return Boolean(section.switch);
    }

    function renderSwitch(section) {
        if (!hasSwitch(section)) {
            return null;
        }

        const isLocked = section.switch === 'locked';
        return (
            <ToggleSwitch
                label={section.title}
                checked={isLocked || isAnalyticsOn}
                disabled={isLocked}
                onChange={isLocked ? undefined : setIsAnalyticsOn}
                enabledText={switchText.enabled}
                disabledText={switchText.disabled}
            />
        );
    }

    function renderSection(section, collapsible) {
        return (
            <CollapsibleSection key={section.id} title={section.title} collapsible={collapsible}>
                {section.paragraphs.map((paragraph) => (
                    <p key={paragraph} className="cc-paragraph">{paragraph}</p>
                ))}
                {renderSwitch(section)}
            </CollapsibleSection>
        );
    }

    function handleTabKeyDown(event, index) {
        const step = TAB_KEYS[event.key];
        if (!step) {
            return;
        }

        event.preventDefault();
        const nextIndex = (index + step + sections.length) % sections.length;
        setActiveIndex(nextIndex);
        tabRefs.current[nextIndex]?.focus();
    }

    const activeSection = sections[activeIndex];

    return (
        <div className="cc-overlay">
            <div ref={dialogRef} className="cc-dialog" role="dialog" aria-modal="true" aria-labelledby={`${idPrefix}-title`}>
                <h2 id={`${idPrefix}-title`} className="cc-sr-only">{dialog.label}</h2>
                <button type="button" className="cc-dialog__close" aria-label={dialog.close} onClick={onClose}>
                    <CloseIcon />
                </button>

                <div className="cc-dialog__brand">
                    <img className="cc-dialog__logo" src={logoSrc} alt={logoAlt} />
                    {!isMobile && (
                        <div className="cc-tabs" role="tablist" aria-orientation="vertical">
                            {sections.map((section, index) => (
                                <button
                                    key={section.id}
                                    ref={(element) => { tabRefs.current[index] = element; }}
                                    type="button"
                                    role="tab"
                                    id={`${idPrefix}-tab-${section.id}`}
                                    aria-selected={index === activeIndex}
                                    aria-controls={`${idPrefix}-pane-${section.id}`}
                                    tabIndex={index === activeIndex ? 0 : -1}
                                    className={`cc-tab${index === activeIndex ? ' is-active' : ''}`}
                                    onClick={() => setActiveIndex(index)}
                                    onKeyDown={(event) => handleTabKeyDown(event, index)}
                                >
                                    {section.title}
                                </button>
                            ))}
                        </div>
                    )}
                    <CookiePolicyLink url={cookiePolicyUrl} className="cc-dialog__policy" />
                </div>

                <div className="cc-dialog__body">
                    {isMobile ? (
                        sections.map((section) => renderSection(section, hasSwitch(section)))
                    ) : (
                        <div
                            role="tabpanel"
                            id={`${idPrefix}-pane-${activeSection.id}`}
                            aria-labelledby={`${idPrefix}-tab-${activeSection.id}`}
                        >
                            {renderSection(activeSection, false)}
                        </div>
                    )}
                </div>

                <div className="cc-dialog__footer">
                    <button type="button" className="cc-button cc-button--primary" onClick={onEnableAll}>
                        {dialog.enableAll}
                    </button>
                    <button type="button" className="cc-button cc-button--primary" onClick={() => onSave(isAnalyticsOn)}>
                        {dialog.save}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CookieDialog;
